// O mundo do Campus Agzos: cidade, prédios, andares e salas, em tiles de 16 px.
//
// Visão de cima em 3/4. Cada sala é uma planta (RoomMap) desenhada em grade de texto;
// o layout (WorldLayout) diz onde cada planta fica. Este arquivo só tem tipos e regras
// puras (sem DOM): o servidor e os testes também usam.

/** Lado de um tile, em pixels de arte. */
export const TILE = 16;
/** Altura da parede norte de uma sala, em pixels (os itens de parede ficam nela). */
export const WALL_H = 24;

/** Para onde o personagem olha. 'right' é 'left' espelhado. */
export type Dir = 'down' | 'up' | 'left' | 'right';

export type RoomKind =
  | 'project' // sala de um projeto: mesas = agentes
  | 'reception'
  | 'kitchen' // copa
  | 'lounge'
  | 'restroom'
  | 'meeting'
  | 'servers'
  | 'terrace'
  | 'hall'; // corredor/escada/elevador

/** Como o personagem ocupa um ponto. */
export type SpotKind = 'desk' | 'chair' | 'sofa' | 'stool' | 'standing';

/** Para que serve um ponto (o comportamento escolhe pelo uso). */
export type SpotUse = 'work' | 'rest' | 'eat' | 'wait' | 'server' | 'meet' | 'restroom';

/** Um móvel colocado numa planta. (x, y) = tile do canto superior esquerdo da pegada. */
export interface Placement {
  /** Id do móvel no catálogo (src/web/art/furniture). */
  item: string;
  x: number;
  y: number;
  /** Espelha na horizontal. */
  flip?: boolean;
}

/** Um ponto onde o personagem para (sentado ou em pé). Coordenadas em tiles. */
export interface Spot {
  id: string;
  x: number;
  y: number;
  facing: Dir;
  kind: SpotKind;
  use: SpotUse;
}

/**
 * Planta de uma sala.
 *
 * `floor` é a grade do piso: uma string por linha, um caractere por tile. ' ' = fora da
 * sala (parede/vazio). `legend` diz qual tile de piso cada caractere usa. A parede norte
 * (WALL_H px) é desenhada acima da linha 0; itens com `wall: true` no catálogo vão nela,
 * com y = 0.
 */
export interface RoomMap {
  id: string;
  kind: RoomKind;
  /** Nome de exibição (placa da sala). */
  name: string;
  floor: readonly string[];
  legend: Readonly<Record<string, string>>;
  items: readonly Placement[];
  /** Tiles de passagem na borda (onde o corredor encosta). */
  doors: ReadonlyArray<{ x: number; y: number }>;
  /** Pontos extras além dos que os móveis já trazem. */
  spots?: readonly Spot[];
  /** Sem parede norte (corredor encostado nas salas). */
  openTop?: boolean;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Uma sala colocada num andar. Coordenadas em tiles, relativas ao prédio. */
export interface PlacedRoom extends Rect {
  /** Id único no mundo (ex.: 'room:agzos-office' ou 'agzos/0/copa'). */
  id: string;
  /** Id da planta usada. */
  mapId: string;
  /** Sala de projeto: o Room.id do escritório. */
  roomId?: string;
  label: string;
}

export interface FloorLayout {
  level: number;
  name: string;
  rooms: PlacedRoom[];
  /** Corredor que liga as salas. */
  corridor: Rect;
}

export interface BuildingLayout extends Rect {
  id: string;
  name: string;
  floors: FloorLayout[];
}

export interface WorldLayout {
  /** Tamanho da cidade, em tiles. */
  w: number;
  h: number;
  buildings: BuildingLayout[];
}

/**
 * Em que prédio fica um projeto: os projetos irmãos com o mesmo prefixo ficam juntos
 * (/var/www/agzos-office e /var/www/agzos-code → prédio "agzos").
 */
export function buildingKeyFor(cwd: string): string {
  const base = cwd.split('/').filter(Boolean).at(-1) ?? '';
  const prefix = base.split(/[-_.]/)[0]?.toLowerCase();
  return prefix || 'geral';
}

/** Tamanho de uma planta, em tiles. */
export function mapSize(map: RoomMap): { w: number; h: number } {
  return { w: Math.max(0, ...map.floor.map((r) => r.length)), h: map.floor.length };
}
